import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  browserSwUpdateEnv,
  createSwUpdate,
  type SwUpdate,
  swUpdateSupported,
} from '../lib/swUpdate';
import { Button } from './ui/Button';

// "A new version is available" prompt (arc 101 slice 09, task 59).
//
// The service worker no longer takes over mid-session (see `public/sw.js`): a new
// build installs and WAITS. This banner is the nudge — when an update is ready it
// pins bottom-centre with a Reload action that hands control to the waiting worker
// (the page reloads onto it). Dismiss just hides the nudge; the update still
// applies on the next natural reload.
//
// Inert unless a prod build has a live SW container to watch, so it renders
// nothing in dev / tests / unsupported browsers (dev deliberately registers no
// worker — see `main.tsx`). Tests inject a fake `update` to drive the UI.

/** Wire the browser SW only in a prod build with a container — else stay inert. */
function defaultUpdate(): SwUpdate | null {
  return import.meta.env.PROD && swUpdateSupported() ? createSwUpdate(browserSwUpdateEnv()) : null;
}

export function UpdatePrompt(props: { readonly update?: SwUpdate }): JSX.Element {
  const update = props.update ?? defaultUpdate();
  if (!update) return null;
  const [dismissed, setDismissed] = createSignal(false);
  return (
    <Show when={update.ready() && !dismissed()}>
      <div class="sw-update" role="status" aria-live="polite">
        <span class="sw-update__msg">{t('update.available')}</span>
        <Button variant="primary" size="sm" onClick={() => update.apply()}>
          {t('update.reload')}
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('toast.dismiss')}
          onClick={() => setDismissed(true)}
        >
          ×
        </Button>
      </div>
    </Show>
  );
}
